import { useState, useEffect, useCallback, useRef } from 'react';
import { Alert } from '@mui/material';
import { UserTableType, UsersTable } from '@/pages/users/UsersTable';
import { AddUserForm } from '@/pages/users/add/AddUserForm';
import { UpdateUserForm } from '@/pages/users/update/UpdateUserForm';
import { UpdateBankAccountForm } from '@/pages/users/bankAccountDetails/UpdateBankAccountForm';
import {
    UserDetailedResponse,
    ZenegyUserDetailed,
    CompanyUsersResponse,
    PaymentsUser
} from '@/models/payment';
import { UsersFilters } from './UsersFilters';
import { useEmployeeFilters } from './useEmployeeFilters';
import { useErrorHandling } from '@/utils/errorHandling';
import { useEmployeesOverview } from '@/pages/users/EmployeesOverview';
import {
    WithConfirmation,
    WithConfirmationWrappedComponentProps
} from '@/utils/withConfirmation';
import { useEmployeeStatusConfirmation } from '@/pages/users/useEmployeeStatusConfirmation';
import { useAppDispatch } from '@/lib/hooks';
import { checkValueAndCall } from '@/lib/functionUtil';
import { getFilterToParams } from '@/pages/users/UserActiveFilter';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { saveRowsPerPageForUserTable } from '@/lib/localStorage';
import { EditUsersDialog } from '@/pages/company/EditUsersDialog';
import { usePagination } from '@/components/shared/TablePagination';
import { useGetCompanyUsers, useGetEmployees } from '@/services/paymentsQuery';

export enum ViewType {
    Normal = 'Normal',
    Admin = 'Admin'
}

interface ZenegyEmployeesProps extends WithConfirmationWrappedComponentProps {
    viewType: ViewType;
}

export const EmployeesOverview = WithConfirmation(
    (props: ZenegyEmployeesProps) => {
        // Use custom hook for filter management
        const {
            selectedEmployeeTypes,
            selectedPandayEmployeeIds,
            selectedSalaryModes,
            selectedSalaryTypes,
            selectedDepartments,
            selectedActiveType,
            selectedInstapaidSalaryTypes,
            username,
            setSelectedEmployeeTypes,
            setPandayEmployeeTypesIds,
            setSelectedSalaryModes,
            setSelectedSalaryTypes,
            setSelectedDepartments,
            setSelectedActiveType,
            setSelectedInstapaidSalaryTypes,
            setUsername,
            filtersKey
        } = useEmployeeFilters();

        const [usersDetailed, setUsersDetailed] =
            useState<UserDetailedResponse<ZenegyUserDetailed>>();

        const [companyUsersDetailed, setCompanyUsersDetailed] =
            useState<CompanyUsersResponse>();

        const [error, setError] = useState<string>();
        const [updateUserId, setUpdateUserId] = useState<string>();
        const [updateUserDialogOpen, setUpdateUserDialogOpen] = useState(false);
        const [accessRowUser, setAccessRowUser] = useState<PaymentsUser>();
        const [editUserAccessOpen, setEditUserAccessOpen] = useState(false);
        const [updateBankAccountUserId, setUpdateBankAccountUserId] =
            useState<string>();
        const [
            updateBankAccountUserDialogOpen,
            setUpdateBankAccountUserDialogOpen
        ] = useState(false);

        const [checked, setChecked] = useState<string[]>([]);

        // Track previous filtersKey to detect actual filter changes
        const prevFiltersKeyRef = useRef<string | null>(null);

        const { backendErrorHandler } = useErrorHandling();
        const {
            onRowClick,
            onCheckAllClick,
            allUsersChecked,
            onCheckAllCompanyUsersClick,
            allCompanyUsersChecked
        } = useEmployeesOverview();

        const { page, setPage, resetPage, rowsPerPage, setRowsPerPage } =
            usePagination();

        const dispatch = useAppDispatch();

        const {
            data: usersDetails,
            isPending: dataPending,
            refetch: refetchUsers,
            isError: usersIsError,
            error: usersError
        } = useGetEmployees(
            {
                salaryModes: selectedSalaryModes,
                departmentIds: selectedDepartments ?? [],
                employeeTypeIdentifiers: selectedEmployeeTypes,
                salaryTypes: selectedSalaryTypes,
                plandayEmployeeTypeIds: selectedPandayEmployeeIds,
                employmentTypes: selectedInstapaidSalaryTypes,
                ...getFilterToParams(selectedActiveType),
                username,
                limit: rowsPerPage > 0 ? rowsPerPage : -1, // can be set to -1 to show all
                offset: page * rowsPerPage
            },
            props?.viewType === ViewType.Normal
        );

        useEffect(() => {
            resetPage();
        }, [filtersKey]);

        useEffect(() => {
            if (usersDetails?.data) {
                setUsersDetailed(usersDetails?.data);
            }
        }, [usersDetails?.data, dataPending]);

        useEffect(() => {
            dispatch(setLoader(dataPending));
        }, [dataPending]);

        useEffect(() => {
            if (usersIsError) {
                backendErrorHandler()(usersError);
            }
        }, [usersIsError, usersError]);

        const reloadUsers = useCallback(() => {
            refetchUsers();
            setChecked([]);
        }, []);

        const {
            data: companyUsersResponse,
            isPending: companyUsersResponseIsPending,
            isError: companyUsersIsError,
            error: companyUsersError,
            refetch: reloadCompanyUsersService
        } = useGetCompanyUsers(
            rowsPerPage > 0 ? rowsPerPage : -1,
            page * rowsPerPage,
            props?.viewType === ViewType.Admin
        );

        useEffect(() => {
            if (props?.viewType === 'Admin') {
                dispatch(setLoader(companyUsersResponseIsPending));
            }
        }, [companyUsersResponseIsPending]);

        useEffect(() => {
            if (companyUsersResponse?.data) {
                setCompanyUsersDetailed(companyUsersResponse?.data);
            }
        }, [companyUsersResponse?.data]);

        useEffect(() => {
            if (companyUsersIsError) {
                backendErrorHandler()(companyUsersError);
            }
        }, [companyUsersIsError, companyUsersError]);

        const reloadCompanyUsers = useCallback(() => {
            reloadCompanyUsersService();
        }, []);

        const onEditClick = (userId: string) => {
            setUpdateUserId(userId);
            setUpdateUserDialogOpen(true);
        };

        const onEditUserAccessClick = (data: any) => {
            setAccessRowUser(data);
            setEditUserAccessOpen(true);
        };

        const onUpdateBankAccountDetailsClick = (userId: string) => () => {
            setUpdateBankAccountUserId(userId);
            setUpdateBankAccountUserDialogOpen(true);
        };

        const handleSalaryModesChange = (value: string | string[]) => {
            checkValueAndCall(value, setSelectedSalaryModes);
        };

        const handleSalaryTypesChange = (value: string | string[]) => {
            checkValueAndCall(value, setSelectedSalaryTypes);
        };

        const handleEmployeeTypesChange = (value: string | string[]) => {
            checkValueAndCall(value, setSelectedEmployeeTypes);
        };

        const handlePlandayEmployeeTypesChange = (value: string | string[]) => {
            checkValueAndCall(value, setPandayEmployeeTypesIds);
        };

        const handleDepartmentChange = (value: string | string[]) => {
            checkValueAndCall(value, setSelectedDepartments);
        };

        const handleSetSelectedActiveType = (value: string | string[]) => {
            checkValueAndCall(value, setSelectedActiveType);
        };

        const handleInstapaidSalaryTypeChange = (value: string | string[]) => {
            checkValueAndCall(value, setSelectedInstapaidSalaryTypes);
        };

        const handleChangePage = (
            event: React.MouseEvent<HTMLButtonElement> | null,
            newPage: number
        ) => {
            setPage(newPage);
            saveRowsPerPageForUserTable(rowsPerPage);
        };

        const handleChangeRowsPerPage = (rowsPerPage: number) => {
            setRowsPerPage(rowsPerPage);
            saveRowsPerPageForUserTable(rowsPerPage);
            setPage(0);
        };

        const {
            handleBulkEnable,
            handleBulkDisable,
            handleStatusChange,
            handleRoleToggleChange,
            sendActivationEmail,
            handleDeleteUser
        } = useEmployeeStatusConfirmation(props.showConfirmDialog, reloadUsers);

        const handleSentActivationEmail = (userId: string, name: string) => {
            sendActivationEmail(userId, name, setError);
        };

        return (
            <>
                {error && <Alert severity="error">{error}</Alert>}

                <div style={{ position: 'relative' }}>
                    <div
                        style={{
                            position: 'absolute',
                            right: 5,
                            top: -80
                        }}
                    >
                        {props?.viewType === 'Normal' && (
                            <AddUserForm
                                onUserListChange={reloadUsers}
                                name="Add user"
                            />
                        )}
                    </div>
                    {props?.viewType === 'Normal' && (
                        <UsersFilters
                            onPlanDayEmployeeTypeChange={
                                handlePlandayEmployeeTypesChange
                            }
                            selectedPlanDayEmployeeTypes={
                                selectedPandayEmployeeIds
                            }
                            selectedDepartments={selectedDepartments}
                            onDepartmentChange={handleDepartmentChange}
                            selectedSalaryModes={selectedSalaryModes}
                            onSalaryModeChange={handleSalaryModesChange}
                            selectedEmployeeTypes={selectedEmployeeTypes}
                            onEmployeeTypeChange={handleEmployeeTypesChange}
                            selectedSalaryTypes={selectedSalaryTypes}
                            onSalaryTypeChange={handleSalaryTypesChange}
                            username={username}
                            onUsernameChange={setUsername}
                            selectedActiveType={selectedActiveType}
                            onActiveTypeChange={handleSetSelectedActiveType}
                            selectedInstaPaidSalaryTypes={
                                selectedInstapaidSalaryTypes
                            }
                            onInstaPaidSalaryTypeChange={
                                handleInstapaidSalaryTypeChange
                            }
                        />
                    )}

                    {(usersDetailed || companyUsersDetailed) && (
                        <UsersTable
                            displayAdmin={
                                props?.viewType === 'Normal' ? false : true
                            }
                            addUserForm={
                                props?.viewType === 'Admin' && (
                                    <AddUserForm
                                        onUserListChange={reloadCompanyUsers}
                                        name="Create user"
                                    />
                                )
                            }
                            total={
                                props?.viewType === 'Normal'
                                    ? usersDetails?.data?.filteredTotal
                                    : companyUsersDetailed?.total
                            }
                            handleDeleteUser={(userId: string) =>
                                handleDeleteUser(
                                    userId,
                                    props?.viewType === 'Normal'
                                        ? reloadUsers
                                        : reloadCompanyUsers
                                )
                            }
                            checked={checked}
                            setChecked={setChecked}
                            allChecked={
                                props?.viewType === 'Normal'
                                    ? allUsersChecked(usersDetailed, checked)
                                    : allCompanyUsersChecked(
                                          companyUsersDetailed,
                                          checked
                                      )
                            }
                            onRowClick={(userId: string) =>
                                onRowClick(userId, checked, setChecked)
                            }
                            onCheckAllClick={() =>
                                props?.viewType === 'Normal'
                                    ? onCheckAllClick(
                                          usersDetailed,
                                          checked,
                                          setChecked
                                      )
                                    : onCheckAllCompanyUsersClick(
                                          companyUsersDetailed,
                                          checked,
                                          setChecked
                                      )
                            }
                            onStatusClick={(
                                userId: string,
                                isActive: boolean
                            ) => {
                                handleStatusChange(
                                    userId,
                                    isActive,
                                    props?.viewType === 'Normal'
                                        ? reloadUsers
                                        : reloadCompanyUsers
                                );
                            }}
                            onRoleToggleChange={(
                                userId: string,
                                isAdmin: boolean
                            ) =>
                                handleRoleToggleChange(
                                    userId,
                                    isAdmin,
                                    props?.viewType === 'Normal'
                                        ? reloadUsers
                                        : reloadCompanyUsers
                                )
                            }
                            onEditClick={
                                props?.viewType === 'Normal'
                                    ? onEditClick
                                    : onEditUserAccessClick
                            }
                            onUpdateBankAccountDetailsClick={
                                onUpdateBankAccountDetailsClick
                            }
                            onSendActivationMailClicked={
                                handleSentActivationEmail
                            }
                            usersDetailed={
                                props?.viewType === 'Normal'
                                    ? usersDetailed
                                    : companyUsersDetailed
                            }
                            handleBulkEnable={handleBulkEnable}
                            handleBulkDisable={handleBulkDisable}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                            rowsPerPage={rowsPerPage}
                            userTableType={UserTableType.Zenergy}
                        />
                    )}
                </div>

                <UpdateUserForm
                    onUserListChange={reloadUsers}
                    userId={updateUserId}
                    open={updateUserDialogOpen}
                    setOpen={(open: boolean) => setUpdateUserDialogOpen(open)}
                />
                <UpdateBankAccountForm
                    onUserListChange={reloadUsers}
                    userId={updateBankAccountUserId}
                    open={updateBankAccountUserDialogOpen}
                    setOpen={(open: boolean) =>
                        setUpdateBankAccountUserDialogOpen(open)
                    }
                />

                <EditUsersDialog
                    dialogOpen={editUserAccessOpen}
                    onClose={() => setEditUserAccessOpen(false)}
                    rowData={editUserAccessOpen && accessRowUser}
                    refreshList={reloadCompanyUsers}
                    reloadCompanyUsersService={reloadCompanyUsersService}
                />
            </>
        );
    }
);
